import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, Marker, Circle, Polyline, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import { Plus, Minus, LocateFixed } from "lucide-react";
import { useApp } from "@/context/AppContext";

let geoPromise = null;
const loadGeo = () => (geoPromise ||= fetch("/geo/sedibeng_municipalities.geojson").then((r) => r.json()));

export const CENTER = [-26.64, 28.08];

const pin = (label, color, { sel, dim, small } = {}) =>
  L.divIcon({
    className: `vig-pin ${small ? "vig-pin-sm" : ""}`,
    html: `<span class="vig-pin-inner ${sel ? "sel" : ""} ${dim ? "dim" : ""}" style="--c:${color}"><span class="dot"></span>${label}</span>`,
    iconSize: [0, 0],
  });
const placeIcon = (name) => L.divIcon({ className: "vig-pin", html: `<span class="vig-place">${name}</span>`, iconSize: [0, 0] });

const Fly = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lng], target.zoom || 11, { duration: 0.8 });
  }, [target, map]);
  return null;
};

const Controls = () => {
  const map = useMap();
  const b = "grid h-8 w-8 place-items-center rounded-md border border-slate-400/30 bg-[#06142a]/85 text-white hover:bg-[#0B1F38]";
  return (
    <div className="absolute bottom-6 right-3 z-[500] flex flex-col gap-1.5">
      <button data-testid="map-zoom-in" className={b} onClick={() => map.zoomIn()}><Plus className="h-4 w-4" /></button>
      <button data-testid="map-zoom-out" className={b} onClick={() => map.zoomOut()}><Minus className="h-4 w-4" /></button>
      <button data-testid="map-reset-view" className={b} onClick={() => map.flyTo(CENTER, 10)}><LocateFixed className="h-4 w-4" /></button>
    </div>
  );
};

/**
 * layers: municipalities | places | initiatives | parcels | concept | corridors | opportunities
 */
export default function VaalMap({ height = 420, layers = ["municipalities", "places", "initiatives", "concept", "corridors"], theme, themesFilter, selectedId, onSelect, flyTo, zoom = 10, testid = "vaal-map", statusFilter }) {
  const { data, themes } = useApp();
  const [geo, setGeo] = useState(null);
  useEffect(() => {
    loadGeo().then(setGeo).catch(() => setGeo(null));
  }, []);
  const has = (k) => layers.includes(k);
  const color = (t) => themes[t]?.color || "#94A3B8";
  const dimmed = (t) => (theme && t !== theme) || (themesFilter && themesFilter.length && !themesFilter.includes(t));
  const inits = useMemo(() => (data.initiatives || []).filter((i) => !statusFilter || statusFilter.includes(i.status)), [data, statusFilter]);

  return (
    <div data-testid={testid} className="relative overflow-hidden rounded-lg" style={{ height }}>
      <MapContainer center={CENTER} zoom={zoom} zoomControl={false} scrollWheelZoom className="h-full w-full" attributionControl>
        <TileLayer className="vig-tiles" url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors · Boundaries &copy; geoBoundaries' maxZoom={18} />
        {has("municipalities") && geo && (
          <GeoJSON data={geo} style={() => ({ color: "#5EEAD4", weight: 1.2, dashArray: "4 4", fillColor: "#0EA5A4", fillOpacity: 0.05 })}
            onEachFeature={(f, l) => l.bindTooltip(`${f.properties.shapeName} Local Municipality`, { sticky: true, className: "vig-tip" })} />
        )}
        {has("corridors") && data.corridors.map((c) => (
          <Polyline key={c.id} positions={c.path} pathOptions={{ color: "#F4B842", weight: 2, dashArray: "6 6", opacity: dimmed("social") ? 0.25 : 0.8 }}>
            <Tooltip className="vig-tip" sticky>{c.name}</Tooltip>
          </Polyline>
        ))}
        {has("concept") && inits.filter((i) => i.radius_km).map((i) => (
          <Circle key={i.id} center={[i.lat, i.lng]} radius={i.radius_km * 1000} eventHandlers={{ click: () => onSelect?.("initiative", i.id) }}
            pathOptions={{ color: color(i.theme), weight: 1.5, dashArray: "5 5", fillOpacity: dimmed(i.theme) ? 0.03 : 0.12 }}>
            <Tooltip className="vig-tip" sticky>{i.location_note}</Tooltip>
          </Circle>
        ))}
        {has("places") && data.places.map((p) => <Marker key={p.id} position={[p.lat, p.lng]} icon={placeIcon(p.name)} interactive={false} />)}
        {has("parcels") && data.parcels.map((p) => (
          <Marker key={p.id} position={[p.lat, p.lng]} icon={pin(`${p.name} · ${p.area_ha.toLocaleString()} ha`, "#FCA5A5", { sel: selectedId === p.id, small: true, dim: dimmed("industrial") })}
            eventHandlers={{ click: () => onSelect?.("parcel", p.id) }}>
            <Tooltip className="vig-tip" direction="bottom" offset={[0, 14]}>Approximate locality marker — not a cadastral boundary</Tooltip>
          </Marker>
        ))}
        {has("initiatives") && inits.map((i) => (
          <Marker key={i.id} position={[i.lat, i.lng]} icon={pin(i.name.replace(/ \(.*\)/, ""), color(i.theme), { sel: selectedId === i.id, dim: dimmed(i.theme) })}
            eventHandlers={{ click: () => onSelect?.("initiative", i.id) }} zIndexOffset={selectedId === i.id ? 1000 : 0} />
        ))}
        {has("opportunities") && data.opportunities.map((o) => (
          <Marker key={o.id} position={[o.lat, o.lng]} icon={pin(o.name, color(o.theme), { sel: selectedId === o.id, small: true, dim: dimmed(o.theme) })}
            eventHandlers={{ click: () => onSelect?.("opportunity", o.id) }} />
        ))}
        <Fly target={flyTo} />
        <Controls />
      </MapContainer>
      <div className="pointer-events-none absolute bottom-1.5 left-2 z-[500] rounded bg-[#06142a]/80 px-2 py-0.5 text-[9.5px] text-slate-300">Markers approximate · areas conceptual · corridors schematic</div>
    </div>
  );
}

export const MapLegend = ({ compact }) => {
  const { data } = useApp();
  return (
    <div data-testid="map-legend" className={`vig-glass grid gap-1.5 p-3 text-[11px] text-slate-200 ${compact ? "grid-cols-2" : ""}`}>
      {data.themes.map((t) => (
        <div key={t.id} className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: t.color }} />{t.short}</div>
      ))}
      <div className="flex items-center gap-2"><span className="w-4 border-t-2 border-dashed border-[#F4B842]" />Schematic corridor</div>
      <div className="flex items-center gap-2"><span className="w-4 border-t border-dashed border-[#5EEAD4]" />Municipal boundary</div>
    </div>
  );
};
